import React, {Suspense, useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {getYYYYMMddFormat, getYYYYMMFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import Popper from '@di-shared/features/common/Popper';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useTripDayEu, {getWeekIndexOfDate, getWeeksOfMonth, isTripOnDay} from '@di-shared/features/history/date/useTripDay.eu';
import {DrivingInfoTripProps} from '@di-shared/features/history/driving-info/DrivingInfoTrip/index';
import DrivingInfoTripContent from '@di-shared/features/history/driving-info/DrivingInfoTripContent';
import useDrivingInfoEu from '@di-shared/features/history/driving-info/useDrivingInfo.eu';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {getWeekIndexOfMonth, isSameDate, isSameYearAndMonth, parseDateWithFormat, startDateOfMonth} from '@shared/dateUtils';

export default function ({vin, selectedDate, date, changeDate}: DrivingInfoTripProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          key={getYYYYMMFormat(selectedDate)}
          vin={vin}
          selectedDate={selectedDate}
          date={date}
          changeDate={changeDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, date, changeDate}: DrivingInfoTripProps) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const navigate = useNavigate();
  const {verifiedDrivingDistances} = useDrivingInfoEu({vin});
  const {focusTripDate, minDate, tripDays} = useTripDayEu({
    vin,
    selectedDate,
    minPeriodDate: verifiedDrivingDistances[0].verifiedDate,
  });

  // URL에서 날짜 복원: date query param이 있고 현재 selectedDate와 같은 월이면 사용
  function getInitialDate() {
    if (date) {
      const parsedDate = parseDateWithFormat(date);
      if (parsedDate && isSameYearAndMonth(parsedDate, selectedDate)) {
        return parsedDate;
      }
    }
    return focusTripDate;
  }

  const [innerDate, setInnerDate] = useState(getInitialDate());

  // 날짜 선택 시 state와 URL query param을 동시에 업데이트
  function updateDateAndUrl(date: Date) {
    setInnerDate(date);
    if (changeDate) {
      changeDate(date);
    }
  }

  return (
    <div className="section_container section_recent_records">
      <div className="section_container_header type_plain">
        <h4 className="title_xxsmall">{t('ctn_mytrips_recent_driving_history')}</h4>
        <DrivingInfoTripTooltip />
      </div>
      <div aria-hidden={true}>
        <ReactDatePicker
          inline
          calendarClassName={'show-header'}
          locale={getDateFnsLocale()}
          disabledKeyboardNavigation
          onChange={() => {}}
          renderCustomHeader={() => null}
          useWeekdaysShort
        />
      </div>
      <Swiper
        key={getYYYYMMFormat(innerDate)}
        initialSlide={getWeekIndexOfDate(innerDate)}
        style={{overflow: 'hidden'}}
        modules={[Pagination]}
        observeParents
        observer
      >
        {getWeeksOfMonth(innerDate).map((_, index) => {
          return (
            <SwiperSlide key={index}>
              {({isActive}) => (
                <div aria-hidden={!isActive}>
                  <ReactDatePicker
                    inline
                    monthAriaLabelPrefix={''}
                    key={getYYYYMMFormat(innerDate)}
                    locale={getDateFnsLocale()}
                    selected={innerDate}
                    minDate={minDate}
                    maxDate={today}
                    filterDate={(date) => isSameYearAndMonth(date, innerDate)}
                    disabledKeyboardNavigation
                    calendarClassName={`show-first-week-${index + 1}`}
                    weekDayClassName={() => 'react-datepicker--disabled'}
                    onChange={updateDateAndUrl}
                    onMonthChange={updateDateAndUrl}
                    renderCustomHeader={() => null}
                    renderDayContents={(dayOfMonth, date: Date) =>
                      !isSameYearAndMonth(date, innerDate) ? null : (
                        <>
                          {dayOfMonth}
                          {isTripOnDay(tripDays, dayOfMonth) && (
                            <span className="has_data">
                              <span className="blind">{t('ctn_home_information')}</span>
                            </span>
                          )}
                        </>
                      )
                    }
                  />
                </div>
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
      <DrivingInfoTripContent
        vin={vin}
        selectedDate={innerDate}
      />
      <button
        className="btn btn_large btn_filled btn_full"
        type="button"
        onClick={() => {
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view driveinfo',
            data_name: t('msg_mytrips_button_text', {month: CldrDatetimeFormatter.format(innerDate, 'MMMM')}),
          });

          navigate(ROUTE_PATHS.HISTORY_$DATE.getFullPath(vin, getYYYYMMddFormat(innerDate)));
        }}
      >
        {t('msg_mytrips_button_text', {month: CldrDatetimeFormatter.format(innerDate, 'MMMM')})}
      </button>
    </div>
  );
}

function FallbackLoading() {
  const today = new Date();
  const {defaultDistanceSymbol, defaultEnergyEfficiencySymbol, defaultSpeedSymbol} = useUnitConverter();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();

  return (
    <div className="section_container skeleton">
      <div className="section_container_header">
        <h4 className="title_xxsmall">{t('ctn_mytrips_recent_driving_history')}</h4>
        <span className="ic ic_more_vertical_20px" />
        <div
          className="download_floating_button_area"
          style={{display: 'none'}}
        >
          <button className="download_btn_item">
            <span className="ic ic_download_20px" />
            <span className="download_btn_text">Download File</span>
          </button>
          <button className="download_btn_item">
            <span className="ic ic_delete_20px" />
            <span className="download_btn_text">Delete Trip</span>
          </button>
        </div>
      </div>
      <ReactDatePicker
        inline
        calendarClassName={'show-header'}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        onChange={() => {}}
        renderCustomHeader={() => null}
        useWeekdaysShort
      />
      <ReactDatePicker
        inline
        calendarClassName={`show-first-week-1`}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        onChange={() => null}
        weekDayClassName={() => 'react-datepicker--disabled'}
        renderCustomHeader={() => null}
      />
      <div className="trip_summary">
        <div className="trip_date skeleton">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
          <span className="trip_date_today">{t('ctn_mytrips_today')}</span>
        </div>
        <div className="trip_summary_list type_multi_line skeleton">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('ctn_mytrips_distance')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('ctn_mytrips_time')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('ctn_mytrips_details_driving_safety_score')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultEnergyEfficiencySymbol}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="trip_info skeleton">
        <span className="blind">trip info detail</span>
        <div className="trip_info_head">
          <span className="trip_info_item">{DashedDatetimeFormatter.formatRange(today, today, 'time')}</span>
          <span className="trip_info_item">
            {ERROR.DASH}
            {defaultDistanceSymbol}
          </span>
        </div>
      </div>
      <ul className="statistics_record_list skeleton">
        <li className="statistics_record_item">
          <span className="statistics_record_key">{t('ctn_mytrips_details_idle_time')}</span>
          <span className="statistics_record_val">
            <span className="statistics_record_number">{ERROR.DASH}</span>
            <span className="statistics_record_unit">{t('ctn_mytrips_details_time_h_01')}</span>
            <span className="statistics_record_number">{ERROR.DASH}</span>
            <span className="statistics_record_unit">{t('ctn_mytrips_details_time_min_01')}</span>
          </span>
        </li>
        <div className="divider divider_vertical" />
        <li className="statistics_record_item">
          <span className="statistics_record_key">{t('ctn_mytrips_details_avg_speed')}</span>
          <span className="statistics_record_val">
            <span className="statistics_record_number">{ERROR.DASH}</span>
            <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
          </span>
        </li>
        <div className="divider divider_vertical" />
        <li className="statistics_record_item">
          <span className="statistics_record_key">{t('ctn_mytrips_details_top_speed')}</span>
          <span className="statistics_record_val">
            <span className="statistics_record_number">{ERROR.DASH}</span>
            <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
          </span>
        </li>
        <div className="divider divider_vertical" />
      </ul>
      <button
        className="btn btn_large btn_filled btn_full"
        type="button"
      >
        {t('msg_mytrips_button_text', {month: CldrDatetimeFormatter.format(today, 'MMMM')})}
      </button>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const today = new Date();
  const startDate = isSameYearAndMonth(selectedDate, today) ? today : startDateOfMonth(selectedDate);
  const weekIndex = getWeekIndexOfMonth(startDate);

  const {defaultDistanceSymbol} = useUnitConverter();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xxsmall">{t('ctn_mytrips_recent_driving_history')}</h4>
        <DrivingInfoTripTooltip />
      </div>
      <ReactDatePicker
        inline
        calendarClassName={'show-header'}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        onChange={() => {}}
        renderCustomHeader={() => null}
        useWeekdaysShort
      />
      <ReactDatePicker
        inline
        calendarClassName={`show-first-week-${weekIndex}`}
        locale={getDateFnsLocale()}
        disabledKeyboardNavigation
        selected={startDate}
        onChange={() => null}
        weekDayClassName={() => 'react-datepicker--disabled'}
        renderCustomHeader={() => null}
      />
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(startDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('ctn_mytrips_details_today')}</span>}
        </div>
        <div className="trip_summary_list type_multi_line">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('ctn_mytrips_distance')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('ctn_mytrips_time')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('ctn_mytrips_driving_safety_score')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('ctn_mytrips_dss_01')}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('msg_mytrips_recent_load_data')}</span>
        </div>
      </div>
      <button
        className="btn btn_large btn_filled btn_full"
        type="button"
      >
        {t('msg_mytrips_button_text', {month: CldrDatetimeFormatter.format(startDate, 'MMMM')})}
      </button>
    </div>
  );
}

function DrivingInfoTripTooltip() {
  const {t} = useInferableTranslation();
  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={3}
      tooltip={
        <div
          role="tooltip"
          tabIndex={0}
        >
          <p
            className="tooltip_text"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_mytrips_monthly_distance_tooltip_eu')}
          </p>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          className="btn_tooltip"
          aria-expanded="false"
          type="button"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          />
        </button>
      </div>
    </Popper>
  );
}
